import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

const getUserEmail = () => {
  try {
    const user = JSON.parse(localStorage.getItem('chatbotUser'));
    return user?.email || null;
  } catch {
    return null;
  }
};

const CallHistory = () => {
  const userEmail = getUserEmail();
  const [callHistory, setCallHistory] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    agentId: '',
    status: '',
    channel: '',
    durationMin: '',
    durationMax: ''
  });
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    dateTime: true,
    agentId: true,
    duration: true,
    status: true,
    channel: true,
    endReason: true,
    callId: true,
    latency: true
  });

  // Pagination
  const recordsPerPage = 10;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(filteredCalls.length / recordsPerPage);
  const paginatedCalls = filteredCalls.slice((page - 1) * recordsPerPage, page * recordsPerPage);

  // Side panel state
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  
  // Transcription state
  const [transcription, setTranscription] = useState(null);
  const [loadingTranscription, setLoadingTranscription] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState(null);

  useEffect(() => {
    if (userEmail) {
      setLoading(true);
      setError(null);
      fetch(`${API_BASE_URL}/api/cached-calls?user_email=${encodeURIComponent(userEmail)}&limit=1000`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            setCallHistory(data);
            setFilteredCalls(data);
          } else setError('Invalid response format');
        })
        .catch(() => setError('Failed to fetch call history'))
        .finally(() => setLoading(false));
    }
  }, [userEmail]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('[data-filter-dropdown]')) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  // Helper functions for formatting
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (typeof timestamp === 'string') {
      // If it's already a string, try to parse it directly
      date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
      // If it's a number, check if it's seconds or milliseconds
      date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
    } else {
      return 'N/A';
    }
    
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
  };
  const formatDuration = (durationMs) => {
    if (!durationMs) return 'N/A';
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Filter functions
  const applyFilters = () => {
    let filtered = [...callHistory];
    
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom).getTime() / 1000;
      filtered = filtered.filter(call => call.start_timestamp >= fromDate);
    }
    
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo).getTime() / 1000;
      filtered = filtered.filter(call => call.start_timestamp <= toDate);
    }
    
    if (filters.agentId) {
      filtered = filtered.filter(call => 
        call.agent_id.toLowerCase().includes(filters.agentId.toLowerCase())
      );
    }
    
    if (filters.status) {
      filtered = filtered.filter(call => 
        call.call_status.toLowerCase().includes(filters.status.toLowerCase())
      );
    }
    
    // if (filters.channel) {
    //   filtered = filtered.filter(call => 
    //     call.channel_type && call.channel_type.toLowerCase().includes(filters.channel.toLowerCase())
    //   );
    // }
    
    if (filters.durationMin) {
      const minDuration = parseInt(filters.durationMin) * 60000; // Convert minutes to ms
      filtered = filtered.filter(call => call.duration_ms >= minDuration);
    }
    
    if (filters.durationMax) {
      const maxDuration = parseInt(filters.durationMax) * 60000; // Convert minutes to ms
      filtered = filtered.filter(call => call.duration_ms <= maxDuration);
    }
    
    setFilteredCalls(filtered);
    setPage(1); // Reset to first page
    setShowFilterDropdown(false);
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      agentId: '',
      status: '',
      channel: '',
      durationMin: '',
      durationMax: ''
    });
    setFilteredCalls(callHistory);
    setPage(1);
    setShowFilterDropdown(false);
  };

  const toggleColumn = (columnName) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  // Handle row click to open side panel
  const handleRowClick = (call) => {
    console.log('📋 [CALL HISTORY] Opening side panel for call:', call);
    setSelectedCall(call);
    setSidePanelOpen(true);
    
    // Fetch call details if call_id is available
    if (call.call_id) {
      fetchCallDetails(call.call_id);
    }
  };

  // Handle closing side panel
  const handleCloseSidePanel = () => {
    console.log('❌ [CALL HISTORY] Closing side panel');
    setSidePanelOpen(false);
    setSelectedCall(null);
    setTranscription(null);
    setTranscriptionError(null);
  };

  // Fetch call details function using Retell AI API
  const fetchCallDetails = async (callId) => {
    console.log('🎤 [CALL HISTORY] Fetching call details for call_id:', callId);
    setLoadingTranscription(true);
    setTranscriptionError(null);
    
    try {
      const apiUrl = `${API_BASE_URL}/api/get-calls/${callId}?user_email=${encodeURIComponent(userEmail)}`;
      console.log('📡 [CALL HISTORY] Fetching call details from Retell AI via:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        console.error('❌ [CALL HISTORY] Call details API response not OK:', response.status, response.statusText);
        throw new Error(`Failed to fetch call details: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📥 [CALL HISTORY] Retell AI call details response:', data);
      
      if (data.success && data.call_details) {
        console.log('✅ [CALL HISTORY] Call details loaded successfully from Retell AI');
        console.log('📊 [CALL HISTORY] Call details structure:', {
          call_id: data.call_details.call_id,
          call_type: data.call_details.call_type,
          agent_id: data.call_details.agent_id,
          agent_name: data.call_details.agent_name,
          call_status: data.call_details.call_status,
          start_timestamp: data.call_details.start_timestamp,
          has_transcript: !!data.call_details.transcript,
          has_recording: !!data.call_details.recording_url,
          has_analysis: !!data.call_details.call_analysis
        });
        setTranscription(data.call_details);
      } else {
        throw new Error(data.error || 'Failed to load call details from Retell AI');
      }
    } catch (error) {
      console.error('❌ [CALL HISTORY] Error fetching call details from Retell AI:', error);
      setTranscriptionError(error.message);
      setTranscription(null);
    } finally {
      setLoadingTranscription(false);
      console.log('🏁 [CALL HISTORY] Call details fetch operation completed');
    }
  };

  // PDF export function
  const handleExportPDF = () => {
    if (filteredCalls.length === 0) {
      alert('No call history data to export.');
      return;
    }

    // Create CSV content first (more reliable for browser export)
    const headers = ['Date/Time', 'Agent ID', 'Duration', 'Status', 'Channel', 'End Reason', 'Call ID', 'Latency'];
    const csvContent = [
      headers.join(','),
      ...filteredCalls.map(call => [
        formatDate(call.start_timestamp),
        call.agent_id,
        formatDuration(call.duration_ms),
        call.call_status,
        call.channel_type || '-',
        call.disconnection_reason || '-',
        call.call_id,
        call.latency ? `${Math.round(call.latency)}ms` : '-'
      ].join(','))
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-history-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);

    // Also try to create a simple HTML-based PDF using jsPDF
    try {
      // Dynamic import of jsPDF to avoid build issues
      import('jspdf').then(({ default: jsPDF }) => {
        const doc = new jsPDF();
        
        // Add title
        doc.setFontSize(16);
        doc.text('Call History Report', 105, 20, { align: 'center' });
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 30, { align: 'center' });
        
        // Add table
        const tableData = filteredCalls.map(call => [
          formatDate(call.start_timestamp),
          call.agent_id,
          formatDuration(call.duration_ms),
          call.call_status,
          call.channel_type || '-',
          call.disconnection_reason || '-',
          call.call_id,
          call.latency ? `${Math.round(call.latency)}ms` : '-'
        ]);

        // Add headers
        const tableHeaders = ['Date/Time', 'Agent ID', 'Duration', 'Status', 'Channel', 'End Reason', 'Call ID', 'Latency'];
        
        // Create table
        doc.autoTable({
          head: [tableHeaders],
          body: tableData,
          startY: 40,
          styles: {
            fontSize: 8,
            cellPadding: 2
          },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          }
        });

        // Add summary
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.text('Summary:', 14, finalY);
        doc.setFontSize(10);
        doc.text(`Total Calls: ${filteredCalls.length}`, 14, finalY + 7);
        doc.text(`Date Range: ${formatDate(filteredCalls[filteredCalls.length - 1]?.start_timestamp)} to ${formatDate(filteredCalls[0]?.start_timestamp)}`, 14, finalY + 14);

        // Save PDF
        doc.save(`call-history-${new Date().toISOString().split('T')[0]}.pdf`);
      }).catch(err => {
        console.log('jsPDF not available, CSV export completed successfully');
      });
    } catch (err) {
      console.log('PDF generation failed, CSV export completed successfully');
    }
  };

  // Sort call records in descending order by created_at
  const sortedCalls = paginatedCalls.sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  return (
    <>
      {/* Standard browser scrollbar styles - like Retell AI */}
      <style>{`
        /* Table container scrollbars - standard browser style */
        .call-history-table-container {
          overflow: auto !important;
        }
        .call-history-table-container::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        .call-history-table-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .call-history-table-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .call-history-table-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .call-history-table-container::-webkit-scrollbar-corner {
          background: #f1f5f9;
        }
        /* Force table to be wider to trigger horizontal scroll */
        .call-history-table {
          min-width: 1200px !important;
        }
        
        /* Side panel styles */
        .side-panel-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1000;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }
        .side-panel-overlay.open {
          opacity: 1;
          visibility: visible;
        }
        .side-panel {
          position: fixed !important;
          top: 0 !important;
          right: -400px !important;
          left: auto !important;
          width: 400px !important;
          height: 100vh !important;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1) !important;
          z-index: 1001 !important;
          transition: right 0.3s ease !important;
          overflow-y: auto !important;
          overflow-x: auto !important;
          -webkit-overflow-scrolling: touch !important;
        }
        .side-panel.open {
          right: 0 !important;
          left: auto !important;
        }
        
        /* Mobile responsive side panel */
        @media screen and (max-width: 768px) {
          .side-panel {
            width: 85vw !important;
            right: -85vw !important;
            max-width: 400px !important;
          }
          .side-panel.open {
            right: 0 !important;
          }
        }
        .side-panel-content {
          padding: 24px;
          min-width: 350px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        
        /* Mobile side panel content */
        @media screen and (max-width: 768px) {
          .side-panel-content {
            padding: 16px;
            min-width: 320px;
          }
        }
        .side-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          background: transparent;
        }
        .side-panel-title {
          font-size: 18px;
          font-weight: 600;
          color: #ffffff;
          margin: 0;
        }
        .side-panel-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #ffffff;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        .side-panel-close:hover {
          background: rgba(255, 255, 255, 0.2);
          color: #ffffff;
        }
        .side-panel-section {
          margin-bottom: 24px;
        }
        .side-panel-section-title {
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .side-panel-field {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }
        .side-panel-field:last-child {
          border-bottom: none;
        }
        .side-panel-field-label {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }
        .side-panel-field-value {
          font-size: 14px;
          color: #ffffff;
          font-weight: 400;
          text-align: right;
          max-width: 200px;
          word-break: break-word;
        }
        
        /* Mobile field value adjustments */
        @media screen and (max-width: 768px) {
          .side-panel-field {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }
          .side-panel-field-value {
            text-align: left;
            max-width: 100%;
          }
        }
        .side-panel-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }
        .side-panel-badge.completed {
          background: #dcfdf4;
          color: #059669;
        }
        .side-panel-badge.pending {
          background: #fef3c7;
          color: #d97706;
        }
        .side-panel-badge.failed {
          background: #fef2f2;
          color: #dc2626;
        }
      `}</style>
      <div className="container d-flex flex-column h-100" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: 'calc(100vh - 80px)' }}>
        <div style={{ background: '#fff', borderRadius: '50px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', padding: '20px', minWidth: '900px', maxWidth: '1100px', width: '100%', color: '#111', marginTop: '0', marginBottom: '10px', paddingTop: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb', gap: 16 }}>
          <h2 style={{ fontWeight: 'bold', fontSize: '1.3rem', color: '#222', margin: 0 }}>Call History</h2>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ position: 'relative' }} data-filter-dropdown>
              <button 
                style={{ background: '#fff', color: '#222', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 18px', fontWeight: 500, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                title="Filter Call History"
              >
                <i className="fas fa-filter"></i> Filter
              </button>
              
              {/* Filter Dropdown */}
              {showFilterDropdown && (
                <div style={{ 
                  position: 'absolute', 
                  top: '100%', 
                  left: 0, 
                  background: '#ffffff', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '12px', 
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', 
                  zIndex: 1000, 
                  width: '400px',
                  maxHeight: '500px',
                  padding: '24px',
                  marginTop: '8px',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e1 #f3f4f6'
                }}>
                  {/* Column Visibility Section */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ 
                      margin: '0 0 16px 0', 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#1f2937',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    }}>
                      Show/Hide Columns
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        fontSize: '14px', 
                        cursor: 'pointer',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={visibleColumns.dateTime} 
                          onChange={() => toggleColumn('dateTime')}
                          style={{ 
                            margin: 0,
                            width: '16px',
                            height: '16px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        Date/Time
                      </label>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        fontSize: '14px', 
                        cursor: 'pointer',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={visibleColumns.agentId} 
                          onChange={() => toggleColumn('agentId')}
                          style={{ 
                            margin: 0,
                            width: '16px',
                            height: '16px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        Agent ID
                      </label>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        fontSize: '14px', 
                        cursor: 'pointer',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={visibleColumns.duration} 
                          onChange={() => toggleColumn('duration')}
                          style={{ 
                            margin: 0,
                            width: '16px',
                            height: '16px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        Duration
                      </label>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        fontSize: '14px', 
                        cursor: 'pointer',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={visibleColumns.status} 
                          onChange={() => toggleColumn('status')}
                          style={{ 
                            margin: 0,
                            width: '16px',
                            height: '16px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        Status
                      </label>
                      {/* <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        fontSize: '14px', 
                        cursor: 'pointer',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={visibleColumns.channel} 
                          onChange={() => toggleColumn('channel')}
                          style={{ 
                            margin: 0,
                            width: '16px',
                            height: '16px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        Channel
                      </label> */}
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        fontSize: '14px', 
                        cursor: 'pointer',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={visibleColumns.endReason} 
                          onChange={() => toggleColumn('endReason')}
                          style={{ 
                            margin: 0,
                            width: '16px',
                            height: '16px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        End Reason
                      </label>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        fontSize: '14px', 
                        cursor: 'pointer',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={visibleColumns.callId} 
                          onChange={() => toggleColumn('callId')}
                          style={{ 
                            margin: 0,
                            width: '16px',
                            height: '16px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        Call ID
                      </label>
                    </div>
                    <div>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}>
                        <input
                          type="checkbox"
                          checked={visibleColumns.latency}
                          onChange={() => toggleColumn('latency')}
                          style={{
                            margin: 0,
                            width: '16px',
                            height: '16px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        Latency
                      </label>
                    </div>
                  </div>

                  {/* Date Range Section */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ 
                      margin: '0 0 16px 0', 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#1f2937',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    }}>
                      Date Range
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          fontSize: '14px', 
                          color: '#6b7280',
                          fontWeight: '500',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}>
                          From:
                        </label>
                        <input
                          type="date"
                          value={filters.dateFrom}
                          onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                          style={{ 
                            width: '100%', 
                            padding: '10px 12px', 
                            border: '1px solid #d1d5db', 
                            borderRadius: '8px', 
                            fontSize: '14px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            backgroundColor: '#ffffff',
                            color: '#374151',
                            outline: 'none',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                      </div>
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          fontSize: '14px', 
                          color: '#6b7280',
                          fontWeight: '500',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                        }}>
                          To:
                        </label>
                        <input
                          type="date"
                          value={filters.dateTo}
                          onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                          style={{ 
                            width: '100%', 
                            padding: '10px 12px', 
                            border: '1px solid #d1d5db', 
                            borderRadius: '8px', 
                            fontSize: '14px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            backgroundColor: '#ffffff',
                            color: '#374151',
                            outline: 'none',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={clearFilters}
                      style={{ 
                        padding: '10px 16px', 
                        border: '1px solid #d1d5db', 
                        background: '#ffffff', 
                        borderRadius: '8px', 
                        cursor: 'pointer', 
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f9fafb';
                        e.target.style.borderColor = '#9ca3af';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#ffffff';
                        e.target.style.borderColor = '#d1d5db';
                      }}
                    >
                      Clear
                    </button>
                    <button
                      onClick={applyFilters}
                      style={{ 
                        padding: '10px 16px', 
                        background: '#3b82f6', 
                        color: '#ffffff', 
                        border: 'none', 
                        borderRadius: '8px', 
                        cursor: 'pointer', 
                        fontSize: '14px',
                        fontWeight: '500',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button 
              style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 22px', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }}
              onClick={handleExportPDF}
              title="Export Call History as PDF"
            >
              Export <i className="fas fa-download" style={{ marginLeft: 8, fontSize: '0.95em' }}></i>
            </button>
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <div className="call-history-table-container" style={{ 
            maxHeight: 'calc(100vh - 300px)', 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'auto'
          }}>
            {loading ? (
              <div style={{ padding: 20, textAlign: 'center' }}>Loading...</div>
            ) : error ? (
              <div style={{ color: 'red', padding: 20, textAlign: 'center' }}>{error}</div>
            ) : paginatedCalls.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center' }}>No call history found.</div>
            ) : (
              <table className="call-history-table" style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                fontSize: '0.97rem',
                minWidth: '1200px'
              }}>
                                  <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f3f4f6' }}>
                    <tr style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '0.97rem' }}>
                      {visibleColumns.dateTime && <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Date/Time</th>}
                      {visibleColumns.agentId && <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Agent ID</th>}
                      {visibleColumns.duration && <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Duration</th>}
                      {visibleColumns.status && <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Status</th>}
                      {/* {visibleColumns.channel && <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Channel</th>} */}
                      {visibleColumns.endReason && <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>End Reason</th>}
                      {visibleColumns.callId && <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Call ID</th>}
                      {visibleColumns.latency && <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Latency</th>}
                    </tr>
                  </thead>
                <tbody>
                  {sortedCalls.map((call, idx) => (
                    <tr key={call.call_id || idx} style={{ 
                      borderBottom: '1px solid #e5e7eb', 
                      background: '#fff', 
                      transition: 'background 0.15s', 
                      fontSize: '0.97rem',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.parentElement.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.target.parentElement.style.background = '#fff'}
                    onClick={() => handleRowClick(call)}
                    >
                      {visibleColumns.dateTime && <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{formatDate(call.created_at)}</td>}
                      {visibleColumns.agentId && <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{call.agent_id}</td>}
                      {visibleColumns.duration && <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{formatDuration(call.duration_ms)}</td>}
                      {visibleColumns.status && <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{call.call_status}</td>}
                      {/* {visibleColumns.channel && <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{call.channel_type || '-'}</td>} */}
                      {visibleColumns.endReason && <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{call.disconnection_reason || '-'}</td>}
                      {visibleColumns.callId && <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{call.call_id}</td>}
                      {visibleColumns.latency && <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: call.latency ? '#059669' : '#64748b', fontWeight: call.latency ? 500 : 400 }}>
                        {call.latency ? `${Math.round(call.latency)}ms` : '-'}
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#64748b', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}>&lt;</button>
          <button style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, width: 36, height: 36, fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>{page}</button>
          <span style={{ color: '#64748b', fontSize: 18 }}>{totalPages > 1 ? '...' : ''}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#64748b', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}>&gt;</button>
        </div>
      </div>
    </div>

    {/* Side Panel */}
    {sidePanelOpen && (
      <>
        <div 
          className={`side-panel-overlay ${sidePanelOpen ? 'open' : ''}`}
          onClick={handleCloseSidePanel}
        />
        <div className={`side-panel ${sidePanelOpen ? 'open' : ''}`}>
          <div className="side-panel-content">
            <div className="side-panel-header">
              <h3 className="side-panel-title">Call Details</h3>
              <button className="side-panel-close" onClick={handleCloseSidePanel}>
                ×
              </button>
            </div>

            {selectedCall && (
              <>
                {/* Basic Information */}
                <div className="side-panel-section">
                  <h4 className="side-panel-section-title">Basic Information</h4>
                  <div className="side-panel-field">
                    <span className="side-panel-field-label">Date/Time</span>
                    <span className="side-panel-field-value">{formatDate(selectedCall.created_at)}</span>
                  </div>
                  <div className="side-panel-field">
                    <span className="side-panel-field-label">Agent ID</span>
                    <span className="side-panel-field-value" style={{ fontFamily: 'monospace' }}>
                      {selectedCall.agent_id}
                    </span>
                  </div>
                  <div className="side-panel-field">
                    <span className="side-panel-field-label">Status</span>
                    <span className="side-panel-field-value">
                      <span className={`side-panel-badge ${selectedCall.call_status || 'pending'}`}>
                        {selectedCall.call_status || 'pending'}
                      </span>
                    </span>
                  </div>
                </div>

                {/* Call Details */}
                <div className="side-panel-section">
                  <h4 className="side-panel-section-title">Call Details</h4>
                  
                  {/* Phone Number Information */}
                  {(selectedCall.from_number || selectedCall.to_number) && (
                    <div className="side-panel-field">
                      <span className="side-panel-field-label">Phone Call</span>
                      <span className="side-panel-field-value" style={{ fontSize: '14px', fontWeight: '500' }}>
                        {selectedCall.from_number && selectedCall.to_number ? (
                          `${selectedCall.from_number} → ${selectedCall.to_number}`
                        ) : (
                          selectedCall.from_number || selectedCall.to_number || 'N/A'
                        )}
                      </span>
                    </div>
                  )}
                  
                  {/* Direction */}
                  {selectedCall.direction && (
                    <div className="side-panel-field">
                      <span className="side-panel-field-label">Direction</span>
                      <span className="side-panel-field-value">
                        <span style={{ 
                          padding: '2px 6px', 
                          borderRadius: '4px', 
                          fontSize: '12px', 
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          background: selectedCall.direction === 'outbound' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                          color: selectedCall.direction === 'outbound' ? '#22c55e' : '#3b82f6'
                        }}>
                          {selectedCall.direction}
                        </span>
                      </span>
                    </div>
                  )}
                  
                  {/* Call Cost */}
                  {selectedCall.call_cost && (
                    <div className="side-panel-field">
                      <span className="side-panel-field-label">Cost</span>
                      <span className="side-panel-field-value" style={{ color: '#ffffff', fontWeight: '500' }}>
                        ${selectedCall.call_cost.combined_cost ? selectedCall.call_cost.combined_cost.toFixed(3) : '0.000'}
                      </span>
                    </div>
                  )}
                  
                  <div className="side-panel-field">
                    <span className="side-panel-field-label">Duration</span>
                    <span className="side-panel-field-value">{formatDuration(selectedCall.duration_ms)}</span>
                  </div>
                  {selectedCall.latency && (
                    <div className="side-panel-field">
                      <span className="side-panel-field-label">Latency</span>
                      <span className="side-panel-field-value" style={{ color: '#059669', fontWeight: '500' }}>
                        {Math.round(selectedCall.latency)}ms
                      </span>
                    </div>
                  )}
                  {selectedCall.call_id && (
                    <div className="side-panel-field">
                      <span className="side-panel-field-label">Call ID</span>
                      <span className="side-panel-field-value" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {selectedCall.call_id}
                      </span>
                    </div>
                  )}
                  {selectedCall.disconnection_reason && (
                    <div className="side-panel-field">
                      <span className="side-panel-field-label">End Reason</span>
                      <span className="side-panel-field-value">
                        {selectedCall.disconnection_reason}
                      </span>
                    </div>
                  )}
                </div>

                {/* Retell AI Call Details Section */}
                {selectedCall.call_id && (
                  <div className="side-panel-section" style={{ 
                    maxHeight: '500px',
                    overflow: 'auto',
                    paddingRight: '8px'
                  }}>
                    <h4 className="side-panel-section-title">Retell AI Call Details</h4>
                    {loadingTranscription ? (
                      <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '14px'
                      }}>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                        Loading call details from Retell AI...
                      </div>
                    ) : transcriptionError ? (
                      <div style={{ 
                        padding: '12px', 
                        background: 'rgba(220, 38, 38, 0.2)', 
                        border: '1px solid rgba(220, 38, 38, 0.3)',
                        borderRadius: '8px', 
                        color: '#ffffff',
                        fontSize: '14px'
                      }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                        {transcriptionError}
                      </div>
                    ) : transcription ? (
                      <div style={{ 
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#ffffff'
                      }}>
                        {/* Call Type and Agent Info */}
                        {transcription.call_type && (
                          <div className="side-panel-field">
                            <span className="side-panel-field-label">Call Type</span>
                            <span className="side-panel-field-value">
                              <span className="side-panel-badge" style={{ 
                                background: transcription.call_type === 'phone_call' ? '#dcfdf4' : '#dbeafe',
                                color: transcription.call_type === 'phone_call' ? '#059669' : '#2563eb'
                              }}>
                                {transcription.call_type === 'phone_call' ? '📞 Phone Call' : '💻 Web Call'}
                              </span>
                            </span>
                          </div>
                        )}
                        
                        {/* Phone Number Information from Retell AI */}
                        {(transcription.from_number || transcription.to_number) && (
                          <div className="side-panel-field">
                            <span className="side-panel-field-label">Phone Call</span>
                            <span className="side-panel-field-value" style={{ fontSize: '14px', fontWeight: '500' }}>
                              {transcription.from_number && transcription.to_number ? (
                                `${transcription.from_number} → ${transcription.to_number}`
                              ) : (
                                transcription.from_number || transcription.to_number || 'N/A'
                              )}
                            </span>
                          </div>
                        )}
                        
                        {/* Direction from Retell AI */}
                        {transcription.direction && (
                          <div className="side-panel-field">
                            <span className="side-panel-field-label">Direction</span>
                            <span className="side-panel-field-value">
                              <span style={{ 
                                padding: '2px 6px', 
                                borderRadius: '4px', 
                                fontSize: '12px', 
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                background: transcription.direction === 'outbound' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                                color: transcription.direction === 'outbound' ? '#22c55e' : '#3b82f6'
                              }}>
                                {transcription.direction}
                              </span>
                            </span>
                          </div>
                        )}
                        
                        {/* Call Cost from Retell AI */}
                        {transcription.call_cost && (
                          <div className="side-panel-field">
                            <span className="side-panel-field-label">Cost</span>
                            <span className="side-panel-field-value" style={{ color: '#ffffff', fontWeight: '500' }}>
                              ${transcription.call_cost.combined_cost ? transcription.call_cost.combined_cost.toFixed(3) : '0.000'}
                            </span>
                          </div>
                        )}
                        
                        {transcription.agent_name && (
                          <div className="side-panel-field">
                            <span className="side-panel-field-label">Agent Name</span>
                            <span className="side-panel-field-value">{transcription.agent_name}</span>
                          </div>
                        )}
                        
                        
                        {/* Dynamic Variables - Filter out sensitive data */}
                        {transcription.retell_llm_dynamic_variables && Object.keys(transcription.retell_llm_dynamic_variables).length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <h5 style={{ 
                              fontSize: '12px', 
                              fontWeight: '600', 
                              color: '#ffffff', 
                              marginBottom: '8px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              Dynamic Variables
                            </h5>
                            {Object.entries(transcription.retell_llm_dynamic_variables)
                              .filter(([key]) => 
                                !['verified_numbers', 'twilio-callsid', 'twilio-accountsid'].includes(key)
                              )
                              .map(([key, value]) => (
                              <div key={key} className="side-panel-field">
                                <span className="side-panel-field-label" style={{ fontSize: '12px' }}>{key}</span>
                                <span className="side-panel-field-value" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                  {typeof value === 'string' ? value : JSON.stringify(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Recording URL */}
                        {transcription.recording_url && (
                          <div style={{ marginTop: '16px' }}>
                            <h5 style={{ 
                              fontSize: '12px', 
                              fontWeight: '600', 
                              color: '#ffffff', 
                              marginBottom: '8px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              Recording
                            </h5>
                            <div style={{ 
                              padding: '8px', 
                              background: 'rgba(255, 255, 255, 0.1)', 
                              borderRadius: '6px',
                              wordBreak: 'break-all'
                            }}>
                              <a 
                                href={transcription.recording_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                  color: '#ffffff', 
                                  textDecoration: 'none',
                                  fontSize: '12px'
                                }}
                              >
                                <i className="fas fa-play-circle" style={{ marginRight: '4px' }}></i>
                                Listen to Recording
                              </a>
                            </div>
                          </div>
                        )}
                        
                        {/* Transcript */}
                        {transcription.transcript && (
                          <div style={{ marginTop: '16px' }}>
                            <h5 style={{ 
                              fontSize: '12px', 
                              fontWeight: '600', 
                              color: '#ffffff', 
                              marginBottom: '8px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              Transcript
                            </h5>
                            <div style={{ 
                              padding: '12px', 
                              background: 'rgba(255, 255, 255, 0.1)', 
                              borderRadius: '6px',
                              maxHeight: '200px',
                              overflow: 'auto',
                              fontSize: '12px',
                              lineHeight: '1.5'
                            }}>
                              {(() => {
                                // Parse transcript to format as conversation
                                const transcriptText = transcription.transcript;
                                
                                // Try to parse if it's JSON format
                                try {
                                  const parsedTranscript = JSON.parse(transcriptText);
                                  if (Array.isArray(parsedTranscript)) {
                                    return (
                                      <div>
                                        {parsedTranscript.map((message, index) => (
                                          <div key={index} style={{ 
                                            marginBottom: '12px',
                                            padding: '8px',
                                            background: message.role === 'user' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '6px',
                                            borderLeft: `3px solid ${message.role === 'user' ? '#ffffff' : 'rgba(255, 255, 255, 0.7)'}`
                                          }}>
                                            <div style={{ 
                                              fontWeight: '600', 
                                              fontSize: '11px', 
                                              color: message.role === 'user' ? '#ffffff' : 'rgba(255, 255, 255, 0.9)',
                                              marginBottom: '4px',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.5px'
                                            }}>
                                              {message.role === 'user' ? '👤 User' : '🤖 Agent'}
                                            </div>
                                            <div style={{ 
                                              color: '#ffffff',
                                              fontSize: '12px',
                                              lineHeight: '1.4',
                                              paddingLeft: '8px'
                                            }}>
                                              {message.content || message.message || message.text}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }
                                } catch (e) {
                                  // If not JSON, try to parse as text with speaker labels
                                  const lines = transcriptText.split('\n').filter(line => line.trim());
                                  const conversation = [];
                                  
                                  for (const line of lines) {
                                    const trimmedLine = line.trim();
                                    if (trimmedLine.toLowerCase().startsWith('user:') || trimmedLine.toLowerCase().startsWith('agent:')) {
                                      const role = trimmedLine.toLowerCase().startsWith('user:') ? 'user' : 'agent';
                                      const content = trimmedLine.substring(role.length + 1).trim();
                                      conversation.push({ role, content });
                                    } else if (conversation.length > 0) {
                                      // Continue previous message
                                      conversation[conversation.length - 1].content += ' ' + trimmedLine;
                                    }
                                  }
                                  
                                  if (conversation.length > 0) {
                                    return (
                                      <div>
                                        {conversation.map((message, index) => (
                                          <div key={index} style={{ 
                                            marginBottom: '12px',
                                            padding: '8px',
                                            background: message.role === 'user' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '6px',
                                            borderLeft: `3px solid ${message.role === 'user' ? '#ffffff' : 'rgba(255, 255, 255, 0.7)'}`
                                          }}>
                                            <div style={{ 
                                              fontWeight: '600', 
                                              fontSize: '11px', 
                                              color: message.role === 'user' ? '#ffffff' : 'rgba(255, 255, 255, 0.9)',
                                              marginBottom: '4px',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.5px'
                                            }}>
                                              {message.role === 'user' ? '👤 User' : '🤖 Agent'}
                                            </div>
                                            <div style={{ 
                                              color: '#ffffff',
                                              fontSize: '12px',
                                              lineHeight: '1.4',
                                              paddingLeft: '8px'
                                            }}>
                                              {message.content}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }
                                }
                                
                                // Fallback: display as plain text
                                return (
                                  <div style={{ 
                                    whiteSpace: 'pre-wrap', 
                                    wordBreak: 'break-word',
                                    color: '#ffffff'
                                  }}>
                                    {transcriptText}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                        
                        {/* Call Analysis */}
                        {transcription.call_analysis && (
                          <div style={{ marginTop: '16px' }}>
                            <h5 style={{ 
                              fontSize: '12px', 
                              fontWeight: '600', 
                              color: '#ffffff', 
                              marginBottom: '8px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              Call Analysis
                            </h5>
                            <div style={{ 
                              padding: '12px', 
                              background: 'rgba(255, 255, 255, 0.1)', 
                              borderRadius: '6px',
                              maxHeight: '150px',
                              overflow: 'auto',
                              fontSize: '12px',
                              lineHeight: '1.5'
                            }}>
                              <pre style={{ 
                                margin: 0, 
                                whiteSpace: 'pre-wrap', 
                                wordBreak: 'break-word',
                                color: '#ffffff'
                              }}>
                                {JSON.stringify(transcription.call_analysis, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                        
                      </div>
                    ) : (
                      <div style={{ 
                        padding: '12px', 
                        background: 'rgba(107, 114, 128, 0.2)', 
                        border: '1px solid rgba(107, 114, 128, 0.3)',
                        borderRadius: '8px', 
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '14px',
                        textAlign: 'center'
                      }}>
                        <i className="fas fa-phone-slash" style={{ marginRight: '8px' }}></i>
                        No call details available from Retell AI
                      </div>
                    )}
                  </div>
                )}

              </>
            )}
          </div>
        </div>
      </>
    )}
    </>
  );
};

export default CallHistory; 